import { Slot } from '@/reservation/reservation.model';

export interface CheckTicketPricesDialogData {
	reservationIds: number[];
	purchasedTickets?: PurchasedTicketSlotInput[];
}

export interface PurchasedTicketSlotInput {
	city: string;
	iataCode?: string | null;
	flightNumber?: string | null;
	price: number;
}

export interface PurchasedTicketGroup {
	city: string;
	iataCode: string | null;
	flightNumber: string | null;
	participantCount: number;
	participantLabel: string;
	price: number;
}

export interface FlightTicketMatchTarget {
	flightNumber: string;
	fromCode: string;
}

/**
 * Maps reservation slots to purchased ticket inputs used in the ticket price dialog.
 *
 * @param slots - Active reservation slots from {@link Slot}.
 * @returns Purchased ticket inputs with city, optional IATA code, flight number and total transport price in grosze.
 */
export function mapReservationSlotsToPurchasedTickets(slots: Slot[]): PurchasedTicketSlotInput[] {
	return slots
		.map((slot) => {
			const city = slot.stopDeparture?.stop?.location?.name?.trim() ?? '';
			return {
				city,
				iataCode: readSlotIataCode(slot),
				flightNumber: slot.stopDeparture?.flightNumber?.trim() || null,
				price: (slot.priceTransportDeparture ?? 0) + (slot.priceTransportReturn ?? 0),
			};
		})
		.filter((ticket) => ticket.city);
}

/**
 * Groups purchased tickets by city and price, counting participants without duplicates.
 *
 * @param tickets - Purchased ticket inputs mapped from reservation slots.
 * @returns Grouped purchased tickets ready to display in the dialog.
 */
export function groupPurchasedTickets(tickets: PurchasedTicketSlotInput[]): PurchasedTicketGroup[] {
	const groups = new Map<string, PurchasedTicketGroup>();

	for (const ticket of tickets) {
		const city = ticket.city.trim();
		if (!city) {
			continue;
		}

		const iataCode = normalizeIataCode(ticket.iataCode);
		const flightNumber = ticket.flightNumber?.trim() || null;
		const key = `${city.toLowerCase()}|${ticket.price}`;
		const existing = groups.get(key);

		if (existing) {
			existing.participantCount += 1;
			existing.participantLabel = formatParticipantCountLabel(existing.participantCount);
			if (!existing.iataCode && iataCode) {
				existing.iataCode = iataCode;
			}
			if (existing.flightNumber && flightNumber && existing.flightNumber !== flightNumber) {
				existing.flightNumber = null;
			}
			continue;
		}

		groups.set(key, {
			city,
			iataCode,
			flightNumber,
			participantCount: 1,
			participantLabel: formatParticipantCountLabel(1),
			price: ticket.price,
		});
	}

	return [...groups.values()];
}

/**
 * Returns purchased ticket groups matching the given flight result.
 *
 * @param groups - Grouped purchased tickets from the reservation.
 * @param flight - Flight result with {@link FlightTicketMatchTarget.fromCode} and {@link FlightTicketMatchTarget.flightNumber}.
 * @returns Purchased ticket groups that match the flight.
 */
export function getPurchasedTicketsForFlight(
	groups: PurchasedTicketGroup[],
	flight: FlightTicketMatchTarget,
): PurchasedTicketGroup[] {
	return groups.filter((group) => doesPurchasedTicketMatchFlight(group, flight));
}

/**
 * Returns purchased ticket groups that do not match any flight result.
 *
 * @param groups - Grouped purchased tickets from the reservation.
 * @param results - Flight search results used to detect available connections.
 * @returns Purchased ticket groups for connections that are no longer available.
 */
export function getUnavailablePurchasedTickets(
	groups: PurchasedTicketGroup[],
	results: FlightTicketMatchTarget[],
): PurchasedTicketGroup[] {
	return groups.filter((group) => !results.some((flight) => doesPurchasedTicketMatchFlight(group, flight)));
}

/**
 * Returns a Polish label for the given participant count.
 *
 * @param count - Number of participants in a purchased ticket group.
 * @returns Label such as `1 uczestnik` or `2 uczestników`.
 */
export function formatParticipantCountLabel(count: number): string {
	if (count === 1) {
		return '1 uczestnik';
	}

	return `${count} uczestników`;
}

function doesPurchasedTicketMatchFlight(group: PurchasedTicketGroup, flight: FlightTicketMatchTarget): boolean {
	const fromCode = normalizeIataCode(flight.fromCode);
	if (group.iataCode && fromCode && group.iataCode === fromCode) {
		return true;
	}

	if (
		group.flightNumber &&
		flight.flightNumber &&
		normalizeFlightNumber(group.flightNumber) === normalizeFlightNumber(flight.flightNumber)
	) {
		return true;
	}

	return false;
}

function readSlotIataCode(slot: Slot): string | null {
	const iataCode = normalizeIataCode(slot.stopDeparture?.stop?.iataCode);
	if (iataCode) {
		return iataCode;
	}

	const locationCode = slot.stopDeparture?.stop?.location?.code?.trim();
	if (locationCode?.length === 3) {
		return locationCode.toUpperCase();
	}

	return null;
}

function normalizeIataCode(value: string | null | undefined): string | null {
	const normalized = value?.trim().toUpperCase();
	if (!normalized) {
		return null;
	}

	return normalized;
}

function normalizeFlightNumber(value: string): string {
	return value.replace(/\s+/g, '').toUpperCase();
}
